import { clsx } from 'clsx'

interface SkeletonBaseProps {
  className?: string
}

interface SkeletonTextProps extends SkeletonBaseProps {
  variant: 'text'
  width?: string
}

interface SkeletonCircleProps extends SkeletonBaseProps {
  variant: 'circle'
  size?: string
}

interface SkeletonRectProps extends SkeletonBaseProps {
  variant: 'rect'
  aspectRatio?: string
}

interface SkeletonCardProps extends SkeletonBaseProps {
  variant: 'card'
}

type SkeletonProps = SkeletonTextProps | SkeletonCircleProps | SkeletonRectProps | SkeletonCardProps

export function Skeleton(props: SkeletonProps) {
  const base = 'animate-pulse bg-slate-200 rounded'

  switch (props.variant) {
    case 'text':
      return (
        <div
          className={clsx(base, 'h-4 rounded-md', props.className)}
          style={{ width: props.width || '100%' }}
        />
      )

    case 'circle':
      return (
        <div
          className={clsx(base, 'rounded-full', props.className)}
          style={{ width: props.size || '40px', height: props.size || '40px' }}
        />
      )

    case 'rect':
      return (
        <div
          className={clsx(base, 'w-full rounded-lg', props.className)}
          style={{ aspectRatio: props.aspectRatio || '4/3' }}
        />
      )

    case 'card':
      return <ListingCardSkeleton className={props.className} />

    default:
      return null
  }
}

export function ListingCardSkeleton({ className }: { className?: string }) {
  return (
    <div className={clsx('card overflow-hidden', className)}>
      {/* Image area */}
      <div className="aspect-[4/3] bg-slate-200 animate-pulse" />

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Price */}
        <div className="h-5 w-24 bg-slate-200 animate-pulse rounded-md" />

        {/* Title - two lines */}
        <div className="space-y-2">
          <div className="h-4 w-full bg-slate-200 animate-pulse rounded-md" />
          <div className="h-4 w-3/4 bg-slate-200 animate-pulse rounded-md" />
        </div>

        {/* Location + views row */}
        <div className="flex items-center justify-between">
          <div className="h-3 w-20 bg-slate-200 animate-pulse rounded-md" />
          <div className="h-3 w-10 bg-slate-200 animate-pulse rounded-md" />
        </div>

        {/* Timestamp */}
        <div className="h-3 w-16 bg-slate-200 animate-pulse rounded-md" />
      </div>
    </div>
  )
}

/** Skeleton for the user profile page */
export function ProfilePageSkeleton() {
  return (
    <div className="container py-8 pb-24 md:pb-8 max-w-3xl">
      {/* Profile header card */}
      <div className="card p-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-slate-200 animate-pulse rounded-full flex-shrink-0" />
          <div className="space-y-2 flex-1">
            <div className="flex items-center gap-2">
              <div className="h-6 w-36 bg-slate-200 animate-pulse rounded-md" />
              <div className="h-5 w-16 bg-slate-200 animate-pulse rounded-full" />
            </div>
            <div className="h-4 w-44 bg-slate-200 animate-pulse rounded-md" />
            <div className="h-3 w-24 bg-slate-200 animate-pulse rounded-md" />
          </div>
        </div>
      </div>

      {/* Quick links grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="card p-4 flex flex-col items-center">
            <div className="w-8 h-8 bg-slate-200 animate-pulse rounded-full mb-2" />
            <div className="h-4 w-20 bg-slate-200 animate-pulse rounded-md" />
          </div>
        ))}
      </div>

      {/* Settings links */}
      <div className="flex gap-4 mb-8">
        <div className="h-4 w-24 bg-slate-200 animate-pulse rounded-md" />
        <div className="h-4 w-32 bg-slate-200 animate-pulse rounded-md" />
      </div>

      {/* Stats card */}
      <div className="card p-6">
        <div className="h-5 w-24 bg-slate-200 animate-pulse rounded-md mb-4" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="text-center">
              <div className="w-10 h-10 mx-auto bg-slate-200 animate-pulse rounded-full mb-2" />
              <div className="h-6 w-10 mx-auto bg-slate-200 animate-pulse rounded-md mb-1" />
              <div className="h-3 w-14 mx-auto bg-slate-200 animate-pulse rounded-md" />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/** Skeleton for the listing detail page */
export function ListingPageSkeleton() {
  return (
    <div className="pb-8">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="container py-3">
          <div className="h-4 w-48 bg-slate-200 animate-pulse rounded-md" />
        </div>
      </div>

      <div className="container py-4 md:py-6">
        <div className="grid lg:grid-cols-2 gap-4 md:gap-8">
          {/* Left column: image */}
          <div>
            <div className="aspect-[4/3] bg-slate-200 animate-pulse rounded-xl mb-4" />
            {/* Thumbnails */}
            <div className="flex gap-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="w-20 h-20 bg-slate-200 animate-pulse rounded-lg flex-shrink-0" />
              ))}
            </div>
          </div>

          {/* Right column: details */}
          <div className="space-y-4">
            {/* Price */}
            <div className="h-8 w-32 bg-slate-200 animate-pulse rounded-md" />

            {/* Title */}
            <div className="space-y-2">
              <div className="h-6 w-full bg-slate-200 animate-pulse rounded-md" />
              <div className="h-6 w-2/3 bg-slate-200 animate-pulse rounded-md" />
            </div>

            {/* Meta row */}
            <div className="flex gap-4">
              <div className="h-4 w-24 bg-slate-200 animate-pulse rounded-md" />
              <div className="h-4 w-16 bg-slate-200 animate-pulse rounded-md" />
              <div className="h-4 w-20 bg-slate-200 animate-pulse rounded-md" />
            </div>

            {/* Seller card */}
            <div className="card p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-200 animate-pulse rounded-full" />
                <div className="space-y-2 flex-1">
                  <div className="h-4 w-28 bg-slate-200 animate-pulse rounded-md" />
                  <div className="h-3 w-20 bg-slate-200 animate-pulse rounded-md" />
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <div className="h-10 flex-1 bg-slate-200 animate-pulse rounded-lg" />
              <div className="h-10 w-20 bg-slate-200 animate-pulse rounded-lg" />
              <div className="h-10 w-10 bg-slate-200 animate-pulse rounded-lg" />
            </div>

            {/* Description */}
            <div className="space-y-2 pt-4">
              <div className="h-5 w-28 bg-slate-200 animate-pulse rounded-md" />
              <div className="h-4 w-full bg-slate-200 animate-pulse rounded-md" />
              <div className="h-4 w-full bg-slate-200 animate-pulse rounded-md" />
              <div className="h-4 w-full bg-slate-200 animate-pulse rounded-md" />
              <div className="h-4 w-3/5 bg-slate-200 animate-pulse rounded-md" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
